import { Link } from 'react-router-dom';
import { ArrowRight, Flower2, BedDouble, CarFront } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { Photo } from '@/components/ui/Photo';
import { Lotus } from '@/components/ui/Lotus';
import { type ImageKey } from '@/data/images';
import { useImages } from '@/lib/images';
import { useReveal } from '@/lib/useReveal';

const CHOICES: {
  to: string; tag: string; title: string; icon: typeof Flower2; imgKey: ImageKey; text: string; cta: string;
}[] = [
  {
    to: '/booking', tag: 'The Spa', title: 'Book a treatment', icon: Flower2, imgKey: 'spaPillar',
    text: 'Massage, facials, nails, and makeup — choose your treatments and a time that suits you.',
    cta: 'Book a treatment',
  },
  {
    to: '/stay/book', tag: 'The Stay', title: 'Book a room', icon: BedDouble, imgKey: 'stayPillar',
    text: 'Self-catering rooms beside the spa, from P200 a night. Pick your dates and we’ll confirm.',
    cta: 'Book a room',
  },
  {
    to: '/cars/book', tag: 'Car Hire', title: 'Hire a car', icon: CarFront, imgKey: 'carPillar',
    text: 'Reliable vehicles by the day — compact, sedan, or 4x4. Pick your dates and we’ll confirm.',
    cta: 'Hire a car',
  },
];

export function BookChoice() {
  useReveal();
  const img = useImages();
  return (
    <>
      <Seo title="Book with us" description="Book a spa treatment or a room at Grace Haven BnB & Spa in Gaborone." />
      <section className="flex min-h-[88vh] items-center bg-cream py-24">
        <Container>
          <div className="text-center">
            <Lotus size={40} className="mx-auto text-gold" />
            <p className="eyebrow mt-4">Grace Haven</p>
            <h1 className="mt-2 font-display text-4xl font-medium text-espresso sm:text-5xl">What would you like to book?</h1>
            <div className="rule-gold mx-auto mt-5" />
            <p className="mx-auto mt-5 max-w-md font-body text-muted">Choose the spa for treatments, or the stay for a room at the BnB.</p>
          </div>

          <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CHOICES.map((c) => (
              <Link key={c.to} to={c.to} className="reveal group relative block overflow-hidden">
                <Photo src={img[c.imgKey]} alt={c.title} className="aspect-[5/4] w-full" imgClassName="transition duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep/92 via-espresso-deep/35 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <c.icon size={26} className="text-gold" strokeWidth={1.5} />
                  <p className="eyebrow mt-3 text-gold-soft">{c.tag}</p>
                  <h2 className="mt-1 font-display text-3xl text-cream">{c.title}</h2>
                  <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-cream/80">{c.text}</p>
                  <span className="btn-gold mt-5 !px-6 !py-2.5">{c.cta} <ArrowRight size={15} /></span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
}
